"use client";

import React, { useEffect, useRef, useState } from "react";

import { iconList } from "@/assets/icons";
import { Button } from "@/components/atoms/button";
import { cn } from "@/lib/utils";
import "@/styles/globals.css";
import type { Swiper as SwiperType } from "swiper";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { SwiperSlide } from "swiper/react";
import { AutoplayOptions } from "swiper/types";
import { Slider } from "@/components/atoms/slider";

/**
 * BannerSlider component
 * Displays a slider for hotel banner images with navigation, dots, and autoplay support.
 * Handles slide change, navigation, and dot rendering logic.
 */

interface IBannerSliderProps {
  children: React.ReactNode;
  containerClassName?: string;
  paginationClassName?: string;
  dotsClassName?: string;
  loop?: boolean;
  isOnlyDots?: boolean;
  autoplay?: boolean | AutoplayOptions;
  wrapperClass?: string;
  initialSlide?: number;
  onSlideChange?: (swiper: SwiperType) => void;
  onSwiper?: (swiper: SwiperType) => void;
}

const BannerSlider: React.FC<IBannerSliderProps> = ({
  children,
  containerClassName = "w-full max-w-[1920px]",
  paginationClassName = "left-1/2",
  dotsClassName,
  loop = true,
  isOnlyDots = false,
  wrapperClass,
  initialSlide,
  autoplay = { delay: 3000, disableOnInteraction: false },
  onSlideChange,
  onSwiper,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);
  const { AngleLeftSmall, AngleRightSmall } = iconList;
  const slides = React.Children.toArray(children);
  const MAX_DOTS = 20;
  const lastInitialSlide = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (swiperRef.current && initialSlide !== undefined) {
      // Sync initial slide if changed
      const currentRealIndex =
        swiperRef.current.realIndex ?? swiperRef.current.activeIndex;
      if (
        lastInitialSlide.current !== initialSlide &&
        currentRealIndex !== initialSlide
      ) {
        swiperRef.current.slideTo(initialSlide);
        lastInitialSlide.current = initialSlide;
      }
    }
  }, [initialSlide]);
  return (
    <div className={cn("relative", containerClassName)}>
      <Slider
        spaceBetween={0}
        slidesPerView={1}
        modules={[Autoplay, Navigation, Pagination]}
        loop={loop}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
          if (onSwiper) {
            onSwiper(swiper);
          }
        }}
        onSlideChange={(swiper) => {
          setActiveIndex(swiper.realIndex || 0);
          if (onSlideChange) {
            onSlideChange(swiper);
          }
        }}
        autoplay={
          autoplay === true
            ? { delay: 3000, disableOnInteraction: false }
            : autoplay || undefined
        }
        wrapperClass={wrapperClass}
      >
        {slides.map((child, index) => {
          const bannerSlideKey = `banner-slide-${index}`;
          return <SwiperSlide key={bannerSlideKey}>{child}</SwiperSlide>;
        })}
      </Slider>
      <div
        className={cn(
          "absolute bottom-4 z-10 flex -translate-x-1/2 transform items-center gap-4 rounded-lg text-white",
          paginationClassName,
        )}
      >
        {!isOnlyDots && (
          <Button
            hierarchy={"ghost"}
            onClick={() => swiperRef.current?.slidePrev()}
            className="cursor-pointer rounded-full !px-1 !py-0 text-white transition hover:bg-[var(--color-overlay-40)] active:bg-[var(--color-overlay-60)]"
            aria-label="Previous slide"
          >
            <AngleLeftSmall />
          </Button>
        )}

        {/* Dots rendering for slide navigation */}
        {/* Dots */}
        {slides.length < MAX_DOTS && (
          <div
            className={cn(
              `flex max-w-[80%] flex-row items-center justify-center gap-2 ${slides?.length > 1 ? "" : "hidden"}`,
              dotsClassName,
            )}
          >
            {slides.map((_, index) => {
              const bannerSlideKey = `banner-slide-second-${index}`;
              const goToSlide = () => {
                return loop
                  ? swiperRef.current?.slideToLoop(index)
                  : swiperRef.current?.slideTo(index);
              };

              return (
                <button
                  key={bannerSlideKey}
                  type="button"
                  onClick={goToSlide}
                  className={`h-2 w-2 rounded-full transition-all ${
                    activeIndex === index
                      ? "h-3 w-3 bg-white"
                      : "h-[9px] w-[9px] bg-gray-400"
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              );
            })}
          </div>
        )}

        {!isOnlyDots && (
          <>
            {/* Page Number */}
            <span className="text-xs font-semibold lg:text-sm">
              {activeIndex + 1}/{slides?.length || 0}
            </span>
            <Button
              hierarchy={"ghost"}
              onClick={() => swiperRef.current?.slideNext()}
              className="cursor-pointer rounded-full !px-1 !py-0 text-white transition hover:bg-[var(--color-overlay-40)] active:bg-[var(--color-overlay-60)]"
              aria-label="Next slide"
            >
              <AngleRightSmall className="text-base" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export { BannerSlider };
